import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import './PriceGraph.css';
import '../LoadingAnimations.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface PriceData {
  max_price: number;
  min_price: number;
  date: string;
}

interface PredictionData {
  [date: string]: number;
}

const PriceGraph: React.FC = () => {
  const { propertyId, unitNumber } = useParams<{ propertyId: string; unitNumber: string }>();
  const [priceData, setPriceData] = useState<PriceData[]>([]);
  const [predictionData, setPredictionData] = useState<PredictionData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<7 | 30 | 90>(7);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const effectivePropertyId = propertyId || localStorage.getItem('selectedPropertyId');
        const effectiveUnitNumber = unitNumber || localStorage.getItem('selectedUnitNumber');

        if (!effectivePropertyId || !effectiveUnitNumber) {
          throw new Error('Missing property or unit information');
        }

        // Fetch historical price data
        const priceResponse = await fetch(
          `https://${process.env.REACT_APP_BASE_URL}/getPrices/${effectivePropertyId}/${effectiveUnitNumber}/${timeRange}`
        );
        const priceData = await priceResponse.json();
        setPriceData(priceData);

        // Fetch prediction data
        const predictionResponse = await fetch(
          `https://${process.env.REACT_APP_BASE_URL}/getPricePredictions/${effectivePropertyId}/${effectiveUnitNumber}`
        );
        const predictionData = await predictionResponse.json();
        console.log('Raw Prediction Data:', predictionData);
        setPredictionData(predictionData);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [propertyId, unitNumber, timeRange]);

  if (isLoading) {
    return (
      <div className="price-graph-container">
        <div className="time-range-toggle">
          <button className="toggle-button">7 Days</button>
          <button className="toggle-button">30 Days</button>
          <button className="toggle-button">90 Days</button>
        </div>
        <div className="price-graph-loading" />
      </div>
    );
  }

  if (!priceData.length) {
    return <div>No price history available</div>;
  }

  // Sort the data by date in ascending order
  const sortedData = [...priceData].sort((a, b) => 
    new Date(a.date + 'T12:00:00Z').getTime() - new Date(b.date + 'T12:00:00Z').getTime()
  );

  // Prepare the chart data
  const chartData = {
    labels: sortedData.map(item => new Date(item.date + 'T12:00:00Z').toLocaleDateString()),
    datasets: [
      {
        label: 'Price',
        data: sortedData.map(item => item.min_price),
        borderColor: '#10b981',
        tension: 0.1,
        borderDash: [] as number[]
      }
    ]
  };

  // Add prediction data if available
  if (predictionData) {
    // Sort prediction dates in ascending order
    const sortedPredictionDates = Object.keys(predictionData).sort((a, b) => 
      new Date(a + 'T12:00:00Z').getTime() - new Date(b + 'T12:00:00Z').getTime()
    );

    // Create a new dataset for the prediction
    const predictionDataset = {
      label: 'Predicted Price',
      data: Array(chartData.labels.length - 1).fill(null).concat([
        sortedData[sortedData.length - 1].min_price,
        ...sortedPredictionDates.map(date => predictionData[date])
      ]),
      borderColor: '#10b981',
      borderDash: [5, 5],
      tension: 0.1
    };

    // Add the prediction dates to the labels
    sortedPredictionDates.forEach(date => {
      chartData.labels.push(new Date(date + 'T12:00:00Z').toLocaleDateString());
    });
    
    chartData.datasets.push(predictionDataset);
  }

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          color: 'white',
          font: {
            size: 14
          }
        }
      },
      title: {
        display: true,
        text: 'Price History',
        color: 'white',
        font: {
          size: 16
        }
      }
    },
    scales: {
      y: {
        beginAtZero: false,
        title: {
          display: true,
          text: 'Price ($)',
          color: 'white',
          font: {
            size: 14
          }
        },
        ticks: {
          color: 'white',
          font: {
            size: 12
          }
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.1)'
        }
      },
      x: {
        title: {
          display: true,
          text: 'Date',
          color: 'white',
          font: {
            size: 14
          }
        },
        ticks: {
          color: 'white',
          font: {
            size: 12
          }
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.1)'
        }
      }
    }
  };

  return (
    <div className="price-graph-container">
      <div className="time-range-toggle">
        <button 
          className={`toggle-button ${timeRange === 7 ? 'active' : ''}`}
          onClick={() => setTimeRange(7)}
        >
          7 Days
        </button>
        <button 
          className={`toggle-button ${timeRange === 30 ? 'active' : ''}`}
          onClick={() => setTimeRange(30)}
        >
          30 Days
        </button>
        <button 
          className={`toggle-button ${timeRange === 90 ? 'active' : ''}`}
          onClick={() => setTimeRange(90)}
        >
          90 Days
        </button>
      </div>
      <div className="chart-container">
        <Line data={chartData} options={options} />
      </div>
    </div>
  );
};

export default PriceGraph; 